import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Users, Clock, XCircle, Sparkles, Zap, Building2, Crown } from "lucide-react";

const PLAN_ICONS = { starter: Sparkles, pro: Zap, studio: Building2, business: Crown };
const PLAN_COLORS = { starter: "#8C8B8A", pro: "#2C4C3B", studio: "#C85A32", business: "#1C1B1A" };

export default function Admin() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/mrr")
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Accesso riservato all'amministratore");
        else setErr(e?.response?.data?.detail || "Errore caricamento");
      });
  }, []);

  if (err) return (
    <div className="p-8 text-center">
      <XCircle size={48} className="mx-auto text-[#C85A32] mb-4" />
      <h2 className="font-display text-2xl">{err}</h2>
    </div>
  );

  if (!data) return <div className="p-8 text-center text-[#8C8B8A]">Caricamento…</div>;

  const stats = [
    { label: "MRR", value: `€${data.mrr_eur.toLocaleString("it-IT")}`, sub: "Ricavo mensile ricorrente", icon: TrendingUp, color: "#2C4C3B" },
    { label: "ARR", value: `€${data.arr_eur.toLocaleString("it-IT")}`, sub: "Proiezione annuale", icon: TrendingUp, color: "#C85A32" },
    { label: "Abbonamenti attivi", value: data.active_subscriptions, sub: "Solo attivi (no trial)", icon: Users, color: "#1C1B1A" },
    { label: "In prova", value: data.trialing, sub: "In trial 7 giorni", icon: Clock, color: "#D4A017" },
    { label: "Cancellazioni", value: data.canceled_total, sub: "Storico", icon: XCircle, color: "#8C8B8A" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <div className="label-mono">Admin dashboard</div>
        <h1 className="font-display text-4xl mt-2 font-light">Business overview</h1>
        <p className="text-sm text-[#5C5B5A] mt-2">Metriche di crescita e distribuzione clienti in tempo reale.</p>
      </div>

      {/* KPI grid */}
      <div data-testid="admin-kpi-grid" className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white border border-[#E5E4E0] p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono text-[10px]">{s.label}</span>
                <Icon size={16} style={{ color: s.color }} />
              </div>
              <div className="font-display text-3xl font-light" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs text-[#8C8B8A] mt-1">{s.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Plan breakdown */}
      <div className="bg-white border border-[#E5E4E0] p-6">
        <h3 className="font-display text-2xl mb-6">Distribuzione per piano</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(data.plan_breakdown).map(([planId, count]) => {
            const Icon = PLAN_ICONS[planId] || Sparkles;
            const color = PLAN_COLORS[planId] || "#8C8B8A";
            return (
              <div key={planId} data-testid={`plan-count-${planId}`} className="border border-[#E5E4E0] p-4">
                <Icon size={20} style={{ color }} />
                <div className="font-display text-2xl mt-2 capitalize">{planId}</div>
                <div className="text-sm text-[#5C5B5A]">{count} {count === 1 ? "cliente" : "clienti"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
